import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import DashboardLayout from "../components/layout/DashboardLayout";
import AdminOverview from "../components/dashboard/admin/AdminOverview";
import AdminUsers from "../components/dashboard/admin/AdminUsers";
import AdminRestaurants from "../components/dashboard/admin/AdminRestaurants";
import AdminCompanies from "../components/dashboard/admin/AdminCompanies";
import AdminCompanyForms from "../components/dashboard/admin/AdminCompanyForms";
import AdminOrders from "../components/dashboard/admin/AdminOrders";
import AdminMap from "../components/dashboard/admin/AdminMap";
import AdminSettings from "../components/dashboard/admin/AdminSettings";
import CompanyOverview from "../components/dashboard/company/CompanyOverview";
import CompanyEmployees from "../components/dashboard/company/CompanyEmployees";
import CompanySettings from "../components/dashboard/company/CompanySettings";
import CompanyPartners from "../components/dashboard/company/CompanyPartners";
import RestaurantAppPreview from "../components/app/RestaurantAppPreview";
import RestaurantOverview from "../components/dashboard/restaurant/RestaurantOverview";
import RestaurantProducts from "../components/dashboard/restaurant/RestaurantProducts";
import RestaurantTodaysSpecial from "../components/dashboard/restaurant/RestaurantTodaysSpecial";
import RestaurantMenus from "../components/dashboard/restaurant/RestaurantMenus";
import RestaurantCatering from "../components/dashboard/restaurant/RestaurantCatering";
import RestaurantAPI from "../components/dashboard/restaurant/RestaurantAPI";
import RestaurantModules from "../components/dashboard/restaurant/RestaurantModules";
import RestaurantPartners from "../components/dashboard/restaurant/RestaurantPartners";
import RestaurantSettings from "../components/dashboard/restaurant/RestaurantSettings";
import RestaurantTables from "../components/dashboard/restaurant/RestaurantTables";
import RestaurantBookings from "../components/dashboard/restaurant/RestaurantBookings";
import { withPrivateRoute } from "../components/HOC/RoutingLogic";
import { useAuth } from "../hooks/useAuth";
import { useSubscription } from "../hooks/useSubscription";
import useLangNavigate from "../hooks/useLangNavigate";
import { Route, Routes, Outlet, useLocation } from "react-router-dom";
import DishView from "../components/dashboard/restaurant/dish/DishView";
import MenuView from "../components/dashboard/restaurant/menu/MenuView";
import CateringView from "../components/dashboard/restaurant/catering/CateringView";
import OfferView from "../components/dashboard/restaurant/offer/OfferView";
import TodaysSpecialView from "../components/dashboard/restaurant/todaysspecial/TodaysSpecialView";
import CompanyView from "../components/dashboard/admin/company/CompanyView";
import useRestaurantActivationStatus from "../hooks/useRestaurantActivationStatus";
import { setRestaurantId } from "../redux/slices/userSlice";
import Modal from "../components/utils/Modal";
import useModal from "../hooks/useModal";
import Button from "../components/buttons/Button";
import RestaurantView from "../components/dashboard/admin/restaurant/RestaurantView";
import CompanyFormView from "../components/dashboard/admin/company/CompanyFormView";
import UserView from "../components/dashboard/admin/user/UserView";
import AdminDevelopers from "../components/dashboard/admin/AdminDevelopers";
import { consumptionIds } from "../utils/constants";
import { isDemoMode } from "../utils/demoApiInterceptor";
import {
  faChartLine,
  faMap,
  faUsers,
  faCode,
  faStore,
  faBuilding,
  faFileLines,
  faCartShopping,
  faGear,
  faUtensils,
  faBowlFood,
  faStar,
  faBookOpen,
  faTruck,
  faBellConcierge,
  faChair,
  faCalendarCheck,
  faHandshake,
  faPuzzlePiece,
  faChartBar,
  faSliders,
} from "@fortawesome/pro-light-svg-icons";
import {
  faChartLine as faChartLineSolid,
  faUtensils as faUtensilsSolid,
  faBowlFood as faBowlFoodSolid,
  faStar as faStarSolid,
  faBookOpen as faBookOpenSolid,
  faTruck as faTruckSolid,
  faBellConcierge as faBellConciergeSolid,
  faChair as faChairSolid,
  faCalendarCheck as faCalendarCheckSolid,
  faHandshake as faHandshakeSolid,
  faPuzzlePiece as faPuzzlePieceSolid,
  faSliders as faSlidersSolid,
  faChartBar as faChartBarSolid,
  faGear as faGearSolid,
} from "@fortawesome/pro-solid-svg-icons";

const Dashboard = () => {
  const [contextualMenu, setContextualMenu] = useState([]);
  const [menuRestaurant, setMenuRestaurant] = useState([]);
  const {
    isOffoodAdmin,
    isCompanyAdmin,
    isRestaurantAdmin,
    isBusinessDeveloper,
  } = useAuth();
  const { hasActiveModule } = useSubscription();
  const { pathname } = useLocation();
  const {
    controlledRestaurantId,
    controlledCompanyId,
    isControllingEntity,
    controlledConsumptionModes,
  } = useSelector((state) => state.admin);
  const dispatch = useDispatch();
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const restaurants = useSelector((state) => state.user.restaurants);
  const { username, isPasswordResetRequired } = useSelector(
    (state) => state.user
  );
  const activationDetails = useRestaurantActivationStatus(restaurantId);
  const { stripeUrl, refreshActivationStatus } = activationDetails;
  const { isModalOpen, openModal, closeModal } = useModal();
  const [modalContent, setModalContent] = useState(null);
  const { t } = useTranslation();
  const navigate = useLangNavigate();

  useEffect(() => {
    if (isPasswordResetRequired)
      navigate(`changeForgotPassword/${username}/required`);
  }, [isPasswordResetRequired, navigate, username]);

  useEffect(() => {
    if (!restaurantId) return;
    const isRestaurantDashboardContext =
      isRestaurantAdmin || (isOffoodAdmin && !!controlledRestaurantId);
    if (!isRestaurantDashboardContext) return;
    const normalized = pathname.replace(/\/$/, "") || "/";
    const isRestaurantOverviewPath =
      /\/dashboard$/.test(normalized) &&
      !normalized.includes("/company/dashboard");
    if (!isRestaurantOverviewPath) return;
    refreshActivationStatus();
  }, [
    pathname,
    restaurantId,
    isRestaurantAdmin,
    isOffoodAdmin,
    controlledRestaurantId,
    refreshActivationStatus,
  ]);

  const showCateringTab = useCallback(() => {
    // In demo mode, always hide catering
    if (isDemoMode()) {
      return false;
    }
    if (controlledRestaurantId) {
      return controlledConsumptionModes.some((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId)
      );
    } else if (!controlledRestaurantId && restaurantId) {
      const restaurant = restaurants?.find(
        (r) => r.restaurantId === restaurantId
      );
      return restaurant?.consumptionmodes?.some((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId)
      );
    }
    return false;
  }, [
    controlledRestaurantId,
    controlledConsumptionModes,
    restaurantId,
    restaurants,
  ]);

  const showRegularMenu = useCallback(() => {
    // In demo mode, always show restaurant features (products, menus, todaysspecials)
    if (isDemoMode()) {
      return true;
    }
    if (controlledRestaurantId) {
      return controlledConsumptionModes.some((mode) =>
        consumptionIds.RESTAURANT.includes(mode.consumptionModeId)
      );
    } else if (!controlledRestaurantId && restaurantId) {
      const restaurant = restaurants?.find(
        (r) => r.restaurantId === restaurantId
      );
      return restaurant?.consumptionmodes?.some((mode) =>
        consumptionIds.RESTAURANT.includes(mode.consumptionModeId)
      );
    }
    return false;
  }, [
    controlledRestaurantId,
    controlledConsumptionModes,
    restaurantId,
    restaurants,
  ]);

  const menuAdmin = useMemo(
    () => [
      {
        key: "adminOverview",
        icon: faChartLine,
        isActive: pathname.endsWith("/dashboard"),
        href: "",
        tKey: "dashboard.admin.",
        element: <AdminOverview tKey={"dashboard.admin."} />,
      },
      {
        key: "adminMap",
        icon: faMap,
        isActive: pathname.includes(`/admin-map`),
        href: "admin-map",
        tKey: "dashboard.adminMap.",
        element: <AdminMap tKey={"dashboard.adminMap."} />,
      },
      {
        key: "adminUsers",
        icon: faUsers,
        isActive: pathname.includes(`/admin-users`),
        href: "admin-users",
        tKey: "dashboard.adminUsers.",
        element: <AdminUsers tKey={"dashboard.adminUsers."} />,
        nested: [
          {
            key: "userView",
            href: ":userId",
            tKey: "dashboard.userView.",
            element: <UserView />,
          },
        ],
      },
      {
        key: "adminDevelopers",
        icon: faCode,
        isActive: pathname.includes(`/admin-developers`),
        href: "admin-developers",
        tKey: "dashboard.adminDevelopers.",
        element: <AdminDevelopers tKey={"dashboard.adminDevelopers."} />,
      },
      {
        key: "adminRestaurants",
        icon: faStore,
        isActive: pathname.includes(`/admin-restaurants`),
        href: "admin-restaurants",
        tKey: "dashboard.adminRestaurants.",
        element: <AdminRestaurants tKey={"dashboard.adminRestaurants."} />,
        nested: [
          {
            key: "restaurantView",
            href: ":restaurantId",
            tKey: "dashboard.restaurantView.",
            element: <RestaurantView />,
          },
        ],
      },
      {
        key: "adminCompanies",
        icon: faBuilding,
        isActive: pathname.includes(`/admin-companies`),
        href: "admin-companies",
        tKey: "dashboard.adminCompanies.",
        element: <AdminCompanies tKey={"dashboard.adminCompanies."} />,
        nested: [
          {
            key: "companyView",
            href: ":companyId",
            tKey: "dashboard.companyView.",
            element: <CompanyView />,
          },
        ],
      },
      {
        key: "adminCompanyForms",
        icon: faFileLines,
        isActive: pathname.includes(`/admin-company-forms`),
        href: "admin-company-forms",
        tKey: "dashboard.adminCompanyForms.",
        element: <AdminCompanyForms tKey={"dashboard.adminCompanyForms."} />,
        nested: [
          {
            key: "companyFormView",
            href: ":companyId",
            tKey: "dashboard.companyFormView.",
            element: <CompanyFormView />,
          },
        ],
      },
      {
        key: "adminOrders",
        icon: faCartShopping,
        isActive: pathname.includes(`/admin-orders`),
        href: "admin-orders",
        tKey: "dashboard.adminOrders.",
        element: <AdminOrders tKey={"dashboard.adminOrders."} />,
      },
      {
        key: "adminSettings",
        icon: faGear,
        isActive: pathname.includes(`/settings`),
        href: "settings",
        tKey: "dashboard.settings.",
        element: <AdminSettings tKey={"dashboard.settings."} />,
        nested: [
          {
            key: "adminSettingsTab",
            href: ":tabName",
            tKey: "dashboard.settings.",
            element: <AdminSettings tKey={"dashboard.settings."} />,
          },
        ],
      },
    ],
    [pathname]
  );

  const menuBusinessDeveloper = useMemo(
    () => [
      {
        key: "businessDeveloperOverview",
        icon: faChartLine,
        isActive: pathname.endsWith("/dashboard"),
        href: "",
        tKey: "dashboard.admin.",
        element: <AdminOverview tKey={"dashboard.admin."} />,
      },
      {
        key: "businessDeveloperRestaurants",
        icon: faStore,
        isActive: pathname.includes(`/admin-restaurants`),
        href: "admin-restaurants",
        tKey: "dashboard.adminRestaurants.",
        element: <AdminRestaurants tKey={"dashboard.adminRestaurants."} />,
        nested: [
          {
            key: "restaurantView",
            href: ":restaurantId",
            tKey: "dashboard.restaurantView.",
            element: <RestaurantView />,
          },
        ],
      },
      {
        key: "adminSettings",
        icon: faGear,
        isActive: pathname.includes(`/settings`),
        href: "settings",
        tKey: "dashboard.settings.",
        element: <AdminSettings tKey={"dashboard.settings."} />,
        nested: [
          {
            key: "adminSettingsTab",
            href: ":tabName",
            tKey: "dashboard.settings.",
            element: <AdminSettings tKey={"dashboard.settings."} />,
          },
        ],
      },
    ],
    [pathname]
  );

  const menuCompany = useMemo(
    () => [
      {
        key: "companyOverview",
        icon: faChartLine,
        isActive: pathname.endsWith("/dashboard"),
        href: "",
        tKey: "dashboard.company.",
        element: <CompanyOverview tKey={"dashboard.company."} />,
      },
      {
        key: "companyEmployees",
        icon: faUsers,
        isActive: pathname.includes(`/employees`),
        href: "employees",
        tKey: "dashboard.employees.",
        element: <CompanyEmployees tKey={"dashboard.employees."} />,
      },
      {
        key: "companyPartners",
        icon: faHandshake,
        isActive: pathname.includes(`/partners`),
        href: "partners",
        tKey: "dashboard.partners.",
        element: <CompanyPartners tKey={"dashboard.partners."} />,
      },
      {
        key: "companySettings",
        icon: faGear,
        isActive: pathname.includes(`/settings`),
        href: "settings",
        tKey: "dashboard.settings.",
        element: <CompanySettings tKey={"dashboard.settings."} />,
        nested: [
          {
            key: "companySettingsTab",
            href: ":tabName",
            tKey: "dashboard.settings.",
            element: <CompanySettings tKey={"dashboard.settings."} />,
          },
        ],
      },
    ],
    [pathname]
  );

  useEffect(() => {
    const menu = [
      {
        key: "restaurantOverview",
        icon: faChartLine,
        iconActive: faChartLineSolid,
        isActive: pathname.endsWith("/dashboard"),
        href: "",
        tKey: "dashboard.restaurant.",
        element: <RestaurantOverview tKey={"dashboard.restaurant."} />,
      },
      {
        key: "menuParent",
        icon: faUtensils,
        iconActive: faUtensilsSolid,
        tKey: "dashboard.menu.",
        hide: !showRegularMenu(),
        children: [
          {
            key: "restaurantProducts",
            icon: faBowlFood,
            iconActive: faBowlFoodSolid,
            isActive: pathname.includes(`/products`),
            href: "products",
            tKey: "dashboard.products.",
            element: <RestaurantProducts tKey={"dashboard.products."} />,
            nested: [
              {
                key: "dishView",
                href: "add",
                tKey: "dashboard.dishView.",
                element: <DishView />,
              },
              {
                key: "dishView",
                href: ":dishId",
                tKey: "dashboard.dishView.",
                element: <DishView />,
              },
            ],
          },
          {
            key: "restaurantTodaysSpecial",
            icon: faStar,
            iconActive: faStarSolid,
            isActive: pathname.includes(`/todaysspecials`),
            href: "todaysspecials",
            tKey: "dashboard.todaysspecials.",
            element: (
              <RestaurantTodaysSpecial tKey={"dashboard.todaysspecials."} />
            ),
            nested: [
              {
                key: "todaysSpecialView",
                href: "add",
                tKey: "dashboard.todaysSpecialView.",
                element: <TodaysSpecialView />,
              },
              {
                key: "todaysSpecialView",
                href: ":todaysSpecialId",
                tKey: "dashboard.todaysSpecialView.",
                element: <TodaysSpecialView />,
              },
            ],
          },
          {
            key: "restaurantMenus",
            icon: faBookOpen,
            iconActive: faBookOpenSolid,
            isActive: pathname.includes(`/menus`),
            href: "menus",
            tKey: "dashboard.menus.",
            element: <RestaurantMenus tKey={"dashboard.menus."} />,
            nested: [
              {
                key: "menuView",
                href: "add",
                tKey: "dashboard.menuView.",
                element: <MenuView />,
              },
              {
                key: "menuView",
                href: ":menuId",
                tKey: "dashboard.menuView.",
                element: <MenuView />,
              },
            ],
          },
        ],
      },
      {
        key: "restaurantCatering",
        icon: faTruck,
        iconActive: faTruckSolid,
        isActive: pathname.includes(`/catering`),
        href: "catering",
        tKey: "dashboard.catering.",
        hide: !showCateringTab(),
        element: <RestaurantCatering tKey={"dashboard.catering."} />,
        nested: [
          {
            key: "cateringView",
            href: "add",
            tKey: "dashboard.cateringView.",
            element: <CateringView />,
          },
          {
            key: "cateringView",
            href: ":dishId",
            tKey: "dashboard.cateringView.",
            element: <CateringView />,
          },
        ],
      },
      {
        key: "serviceParent",
        icon: faBellConcierge,
        iconActive: faBellConciergeSolid,
        tKey: "dashboard.service.",
        hide: !hasActiveModule("tables"),
        children: [
          {
            key: "restaurantTables",
            icon: faChair,
            iconActive: faChairSolid,
            isActive: pathname.includes(`/tables`),
            href: "tables",
            tKey: "dashboard.tables.",
            element: <RestaurantTables tKey={"dashboard.tables."} />,
          },
          {
            key: "restaurantBookings",
            icon: faCalendarCheck,
            iconActive: faCalendarCheckSolid,
            isActive: pathname.includes(`/bookings`),
            href: "bookings",
            tKey: "dashboard.bookings.",
            element: <RestaurantBookings tKey={"dashboard.bookings."} />,
          },
        ],
      },
      {
        key: "restaurantPartners",
        icon: faHandshake,
        iconActive: faHandshakeSolid,
        isActive: pathname.includes(`/partners`),
        href: "partners",
        tKey: "dashboard.partners.",
        element: <RestaurantPartners tKey={"dashboard.partners."} />,
        hide: !showRegularMenu(),
        nested: [
          {
            key: "offerView",
            href: "add",
            tKey: "dashboard.offerView.",
            element: <OfferView />,
          },
          {
            key: "offerView",
            href: ":offerId",
            tKey: "dashboard.offerView.",
            element: <OfferView />,
          },
        ],
      },
      {
        key: "restaurantModules",
        icon: faPuzzlePiece,
        iconActive: faPuzzlePieceSolid,
        isActive: pathname.includes(`/modules`),
        href: "modules",
        tKey: "dashboard.modules.",
        element: <RestaurantModules tKey={"dashboard.modules."} />,
      },
      {
        key: "managementParent",
        icon: faSliders,
        iconActive: faSlidersSolid,
        tKey: "dashboard.management.",
        children: [
          {
            key: "restaurantReports",
            icon: faChartBar,
            iconActive: faChartBarSolid,
            href: stripeUrl,
            tKey: "dashboard.reports.",
            outsideLink: true,
          },
          {
            key: "restaurantSettings",
            icon: faGear,
            iconActive: faGearSolid,
            isActive: pathname.includes(`/settings`),
            href: "settings",
            tKey: "dashboard.settings.",
            element: <RestaurantSettings tKey={"dashboard.settings."} />,
            nested: [
              {
                key: "restaurantSettingsTab",
                href: ":tabName",
                tKey: "dashboard.settings.",
                element: <RestaurantSettings tKey={"dashboard.settings."} />,
              },
            ],
          },
        ],
      },
      {
        key: "restaurantAPI",
        isActive: pathname.includes(`/api`),
        href: "api",
        tKey: "dashboard.api.",
        element: <RestaurantAPI tKey={"dashboard.api."} />,
        hide: true,
      },
    ];
    setMenuRestaurant(menu);
  }, [
    restaurants,
    restaurantId,
    controlledConsumptionModes,
    pathname,
    stripeUrl,
    showCateringTab,
    showRegularMenu,
    hasActiveModule,
  ]);

  useEffect(() => {
    if (isBusinessDeveloper && !isControllingEntity) {
      setContextualMenu(menuBusinessDeveloper);
    } else if (isCompanyAdmin || !!controlledCompanyId) {
      setContextualMenu(menuCompany);
    } else if (isRestaurantAdmin || !!controlledRestaurantId) {
      setContextualMenu(menuRestaurant);
    } else if (isOffoodAdmin && !isControllingEntity) {
      setContextualMenu(menuAdmin);
    }
  }, [
    menuAdmin,
    menuCompany,
    menuRestaurant,
    menuBusinessDeveloper,
    isControllingEntity,
    isOffoodAdmin,
    isCompanyAdmin,
    isRestaurantAdmin,
    isBusinessDeveloper,
    controlledRestaurantId,
    controlledCompanyId,
  ]);

  const handleSelectRestaurant = useCallback(
    (restaurantId) => {
      dispatch(setRestaurantId(restaurantId));
      closeModal();
    },
    [closeModal, dispatch]
  );

  useEffect(() => {
    if (!restaurantId && restaurants.length > 1) {
      setModalContent(
        <>
          <h3 className="mt-0">{t("utils.common.chooseRestaurant")}</h3>
          <div className="flex column gap-1">
            {restaurants.map((restaurant) => (
              <Button
                variant="danger"
                className="p-4 justify-start gap-2"
                block
                onClick={() => handleSelectRestaurant(restaurant.restaurantId)}
                key={restaurant.restaurantId}
              >
                <RestaurantAppPreview
                  size="45"
                  img={restaurant.images?.[0]?.fullImagePath}
                  restaurantType={restaurant.restauranttype}
                />
                <span>{restaurant.name}</span>
              </Button>
            ))}
          </div>
        </>
      );
      openModal();
    }
  }, [restaurants, restaurantId, t, handleSelectRestaurant, openModal]);

  // Flatten menu items for routing (extract children from collapsible items)
  const flattenedRouteItems = useMemo(() => {
    const items = [];
    contextualMenu.forEach((item) => {
      if (item.children) {
        // For collapsible items, add all children as route items
        item.children.forEach((child) => {
          if (!child.hide && child.element) {
            items.push(child);
          }
        });
      } else if (!item.hide && item.element) {
        // Regular items with elements
        items.push(item);
      }
    });
    return items;
  }, [contextualMenu]);

  const renderedRoutes = () => {
    return flattenedRouteItems.map((item) => {
      const nestedRoutes = item.nested?.map((nestedItem) => (
        <Route
          key={nestedItem.key}
          path={nestedItem.href}
          element={nestedItem.element}
        />
      ));
      return (
        <Route key={item.key} path={`${item.href}/*`} element={<Outlet />}>
          <Route index element={item.element} />
          {nestedRoutes}
        </Route>
      );
    });
  };

  const findParentRoute = (pathname) => {
    const pathSegments = pathname.split("/").filter(Boolean);
    const isDemo = pathname.includes("/demo");

    // In demo mode: /lang/demo/dashboard/products/1 -> [lang, demo, dashboard, products, 1]
    // Normal mode: /lang/dashboard/products/1 -> [lang, dashboard, products, 1]
    let dashboardIndex = pathSegments.indexOf("dashboard");

    if (dashboardIndex === -1 || pathSegments.length <= dashboardIndex + 1) {
      return null;
    }

    const parentSegment = pathSegments[dashboardIndex + 1];
    const basePath = isDemo ? "/demo/dashboard" : "/dashboard";
    return `${basePath}/${parentSegment}`;
  };

  return (
    <DashboardLayout
      menuList={contextualMenu}
      navigatePath={findParentRoute(pathname)}
      activationDetails={activationDetails}
    >
      <Routes>
        <Route path="/" element={<Outlet />}>
          {renderedRoutes()}
        </Route>
      </Routes>
      <Modal isOpen={isModalOpen} noFooter noBgClickable>
        {modalContent}
      </Modal>
    </DashboardLayout>
  );
};

export default withPrivateRoute(Dashboard);
